import { FC, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Typography } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { clickableProps } from '../../helpers/interactive'
import { linkTypographyBoldSx } from '../../helpers/styles'
import { GameIconThumb, motivationAvailabilityColumn, trackedGameCountColumn, trackedGameGenreColumn } from '../../helpers/trackedGameColumns'
import { useAllGamesListing } from '../../hooks/useAllGamesListing'
import { useControlledColumns } from '../../hooks/useControlledColumns'
import { useGamesListingFilter } from '../../hooks/useGamesListingFilter'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { TrackedGame } from '../../types'
import { GamesListingFilterBar } from '../GamesListingFilterBar/GamesListingFilterBar'
import { SectionCard } from '../SectionCard/SectionCard'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'
import { UAInsightsDialog } from '../UAInsightsDialog/UAInsightsDialog'

const gameRowId = (row: TrackedGame) => row.id

/**
 * Drill-down dialog reached from the "Games Tracked" summary card. Lists every
 * tracked game (network-scoped when a network filter is active) with clickable
 * game name, genre and creatives count so the user can pivot into the existing
 * game / genre / creative drill-downs.
 */
export const AllGamesDialog: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { params, close, canGoBack, goBack, openGame, openGenreTags } = useUAInsightsNavigation()
  const { genreLabel } = useUATaxonomy()
  const open = params.dialog === 'all-games'
  const { data, isLoading, isError, refetch } = useAllGamesListing(open)

  const handleGameClick = useCallback((gameId: string) => openGame(gameId), [openGame])
  const handleGenreClick = useCallback((genreId: string) => openGenreTags(genreId), [openGenreTags])

  const games = useMemo(() => data?.games ?? [], [data?.games])

  // Seed the in-dialog genre filter from the dashboard's selection so the
  // listing opens scoped to the same genre the user was already looking at,
  // while still letting them switch to "All Genres" (or another genre)
  // in-place without closing the dialog.
  const { search, genreFilter, setGenreFilter, availableGenres, filteredGames, handleSearchChange, handleGenreChange } = useGamesListingFilter(
    games,
    params.genreFilter ?? ''
  )

  // Re-sync if the dashboard genre filter changes while the dialog is mounted
  // but closed — opening it again should still reflect the latest dashboard
  // scope rather than a stale value from a previous open.
  useEffect(() => {
    if (!open) setGenreFilter(params.genreFilter ?? '')
  }, [open, params.genreFilter, setGenreFilter])

  const defaultColumns: GRTableColumn<TrackedGame, undefined>[] = useMemo(
    () => [
      {
        labelAccessor: t('col_game_name'),
        accessor: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GameIconThumb src={row.icon} alt={row.name} />
            <Typography variant="body2" sx={linkTypographyBoldSx} {...clickableProps(() => handleGameClick(row.id))}>
              {row.name}
            </Typography>
          </Box>
        ),
        sortAccessor: 'name',
        sortable: true,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
      },
      trackedGameGenreColumn(t, genreLabel, handleGenreClick),
      motivationAvailabilityColumn(t),
      trackedGameCountColumn(t('col_creatives'), 'creativesCount', handleGameClick, { sortOrder: SortOrder.DESC }),
      trackedGameCountColumn(t('col_unique_tags'), 'uniqueTagsCount', handleGameClick),
    ],
    [t, genreLabel, handleGameClick, handleGenreClick]
  )

  const [columns, setColumns] = useControlledColumns(defaultColumns)

  const headerTitle = (
    <GamesListingFilterBar
      search={search}
      onSearchChange={handleSearchChange}
      genreFilter={genreFilter}
      onGenreChange={handleGenreChange}
      genres={availableGenres}
    />
  )

  const headerAction = (
    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
      {t('games_count_section', { count: filteredGames.length })}
    </Typography>
  )

  return (
    <UAInsightsDialog
      open={open}
      onClose={close}
      title={t('all_games_title')}
      canGoBack={canGoBack}
      onGoBack={goBack}
      showNetworkSelector
      hasData={!!data}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      renderChildrenWhenEmpty
      loadingPlaceholder={<TableSkeleton rows={10} columns={5} />}
    >
      <SectionCard title={headerTitle} headerAction={headerAction} disableBodyPadding>
        <GRTable rows={filteredGames} columns={columns} onColumnsUpdated={setColumns} striped hoverable rowIdKey={gameRowId} noRowsLabel={t('no_games')} />
      </SectionCard>
    </UAInsightsDialog>
  )
}
